import { useTable } from '@refinedev/core';
import { ChevronLeft, ChevronRight, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { STATUS_LABEL, STATUS_VARIANT, formatBalances, formatRelative } from '@/lib/format';
import { ACCOUNT_STATUSES, REGIONS, type PsnAccount } from '@/lib/types';

const ALL = '__all__';

export function PsnAccountsListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [regionFilter, setRegionFilter] = useState<string>(ALL);

  const { tableQueryResult, current, setCurrent, pageSize, setFilters } = useTable<PsnAccount>({
    resource: 'psn-accounts',
    pagination: { current: 1, pageSize: 50 },
  });

  const applyFilters = () => {
    const filters = [];
    if (statusFilter !== ALL) filters.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    if (regionFilter !== ALL) filters.push({ field: 'region', operator: 'eq' as const, value: regionFilter });
    if (search.trim()) filters.push({ field: 'search', operator: 'eq' as const, value: search.trim() });
    setFilters(filters, 'replace');
    setCurrent(1);
  };

  const data = tableQueryResult.data?.data ?? [];
  const total = tableQueryResult.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Contas PSN</h1>
          <p className="text-muted-foreground">Pool de contas doadoras — {total} no total.</p>
        </div>
        <Button asChild>
          <Link to="/contas/new">
            <Plus className="h-4 w-4" />
            Nova conta
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por email…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos status</SelectItem>
            {ACCOUNT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={regionFilter} onValueChange={setRegionFilter}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Região" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todas</SelectItem>
            {REGIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="secondary" onClick={applyFilters}>
          Aplicar
        </Button>
        {/* Q-246: filtro rápido da fila de import */}
        <Button
          variant={statusFilter === 'game_purchased' ? 'default' : 'outline'}
          size="sm"
          className="rounded-full"
          onClick={() => {
            const next = statusFilter === 'game_purchased' ? ALL : 'game_purchased';
            setStatusFilter(next);
            const filters = [];
            if (next !== ALL) filters.push({ field: 'status', operator: 'eq' as const, value: next });
            if (regionFilter !== ALL)
              filters.push({ field: 'region', operator: 'eq' as const, value: regionFilter });
            if (search.trim())
              filters.push({ field: 'search', operator: 'eq' as const, value: search.trim() });
            setFilters(filters, 'replace');
            setCurrent(1);
          }}
        >
          Prontas pra importar
        </Button>
        {(statusFilter !== ALL || regionFilter !== ALL || search) && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('');
              setStatusFilter(ALL);
              setRegionFilter(ALL);
              setFilters([], 'replace');
              setCurrent(1);
            }}
          >
            Limpar
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="w-20">Região</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-36">Saldo</TableHead>
              <TableHead className="w-28">PSN ID</TableHead>
              <TableHead className="w-32">Criada</TableHead>
              <TableHead className="w-32">Última ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableQueryResult.isLoading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  Nenhuma conta encontrada.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/contas/${row.id}`)}
                >
                  <TableCell className="font-mono text-sm">{row.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.region}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatBalances(row.balance)}</TableCell>
                  <TableCell className="text-muted-foreground">{row.psn_online_id ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{formatRelative(row.created_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{formatRelative(row.last_action_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Página {current} de {totalPages} ({total} no total)
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={current <= 1}
            onClick={() => setCurrent(current - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={current >= totalPages}
            onClick={() => setCurrent(current + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
